import { TwoFactorT } from 'types/twoFactor'
// import * as Sentry from '@sentry/react'
import get from 'lodash/get'
import omitBy from 'lodash/omitBy'
import isNil from 'lodash/isNil'
import PACKAGE from '../../package.json'
import jsLogger from 'js-logger'
import config from 'src/config'
import { versions } from 'components/versions'
import { ConversationStartResponse } from 'types/twoFactorProto'

const headers = {
  'Content-Type': 'application/json',
  Accept: 'application/json'
}

const mimeToExtMap = {
  'video/mp4': 'mp4',
  'video/webm': 'webm',
  'video/ogg': 'ogg',
  'video/quicktime': 'mov',
  'video/x-matroska': 'mkv',
  'video/x-msvideo': 'avi'
}

export const getNavigatorLanguage = () => {
  if (navigator.languages && navigator.languages.length) {
    return navigator.languages[0]
  } else {
    return (
      get(navigator, 'userLanguage') ||
      get(navigator, 'language') ||
      get(navigator, 'browserLanguage') ||
      'en'
    )
  }
}

export const conversationStart = async (
  variant: TwoFactorT.VariantT,
  urlLang: string | null
) => {
  try {
    // demo2 start now lives on the always-warm socket VM (no FF cold start on the
    // pre-render critical path). socketServerUrl carries no scheme — derive it:
    // https for the dev VM, http for the local socket (localhost is a secure-
    // context exception, so http from the https dev page is not blocked).
    const socketHost = config.socketServerUrl.replace(/^https?:\/\//, '')
    const socketBase =
      socketHost.startsWith('localhost') || socketHost.startsWith('127.')
        ? `http://${socketHost}`
        : `https://${socketHost}`
    const url = `${socketBase}/demo2/start`
    const browserLang = getNavigatorLanguage()
    const windowUrl = new URL(window.location.href)
    const transcriber = windowUrl.searchParams.get('t')
    const isBot = windowUrl.searchParams.get('bot') === '1'
    // const pageUrl = new URL(window.location.href)
    const params = {
      //comment out the param to send slack notifictions about local sessions
      slackNotificationsDisabled:
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1',
      variant,
      version: PACKAGE.version,
      browserLang,
      urlLang,
      customTranscriber: transcriber,
      libVersions: versions,
      ...(isBot && { isBot: true })
    }
    const req = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(params)
    })
    jsLogger.log('conversationStart', params)
    const res = (await req.json()) as ConversationStartResponse
    jsLogger.log('conversationStart res', res)
    return res
  } catch (e) {
    jsLogger.error('interactionStart error', e)
    // Sentry.captureException(e)
    return
  }
}

export const getRecapInfo = async (interactionId: string) => {
  const url = `${config.apiUrl}/two_factor/${interactionId}/recap`
  const req = await fetch(url, {
    method: 'GET',
    priority: 'low',
    headers
  })
  if (req.ok) {
    const { interaction } = (await req.json()) as {
      interaction: TwoFactorT.RecapT
    }
    jsLogger.log('getRecapInfo res', interaction)
    return interaction
  } else {
    jsLogger.warn('getRecapInfo error', req.statusText)
    return null
  }
}

export const dbGetLocalization = async () => {
  const url = `${config.apiUrl}/demo2/localization`
  const req = await fetch(url, {
    method: 'GET',
    priority: 'low',
    headers
  })
  if (req.ok) {
    const res = (await req.json()) as Record<string, Record<string, string>>
    jsLogger.log('dbGetLocalization res', res)
    return res
  } else {
    jsLogger.warn('dbGetLocalization error', req.statusText)
    return null
  }
}

export const generateId = (): string => {
  const currentDate = new Date()
  const year = currentDate.getFullYear()
  const month = (currentDate.getMonth() + 1).toString().padStart(2, '0')
  const day = currentDate.getDate().toString().padStart(2, '0')
  const hours = currentDate.getHours().toString().padStart(2, '0')
  const minutes = currentDate.getMinutes().toString().padStart(2, '0')
  const seconds = currentDate.getSeconds().toString().padStart(2, '0')
  const milliseconds = currentDate.getMilliseconds().toString().padStart(3, '0')
  const id = `${year}${month}${day}${hours}${minutes}${seconds}${milliseconds}`
  return id
}

export const uploadBlobToStorage = async (
  blob: Blob,
  storagePath: string
): Promise<void> => {
  const extension = get(mimeToExtMap, blob.type.split(';')[0]) || 'webm'
  const storageEndpoint = `https://firebasestorage.googleapis.com/v0/b/${
    config.firebaseStorageId
  }/o/${encodeURIComponent(storagePath)}.${extension}`
  try {
    jsLogger.log('Blob details:', {
      type: blob.type,
      size: blob.size,
      extension
    })
    const responseBlob = await fetch(storageEndpoint, {
      method: 'POST',
      headers: {
        'Content-Type': blob.type
      },
      body: blob
    })

    if (!responseBlob.ok) {
      throw new Error(
        `Failed to upload Blob to storage: ${responseBlob.statusText}`
      )
    }

    jsLogger.log('Blob uploaded successfully')
  } catch (error) {
    jsLogger.error('Error uploading Blob to storage:', error)
  }
}

// export const sendError = async (
//   event: Sentry.Event,
//   hint: Sentry.EventHint
// ) => {
//   const url = `${config.apiUrl}/two_factor/error`
//   const req = await fetch(url, {
//     method: 'POST',
//     priority: 'low',
//     body: JSON.stringify({ event, hint }),
//     headers
//   })
//   const data = await req.json()

//   jsLogger.log('sendError response', data)
//   return data
// }

export const flushLogs = async (interactionId: string) => {}

export const checkIsWebMSupported = () => {
  var video = document.createElement('video')

  return video.canPlayType('video/webm') !== ''
}

export const validateEmail = async (email: string) => {
  const source = 'code'
  const url = 'https://p-gpdh46ebpa-uc.a.run.app/validateEmail'
  const req = await fetch(url, {
    method: 'POST',
    body: JSON.stringify({ email, source })
  })
  const data = await req.json()
  jsLogger.log('validateEmail response', data)
  return data
}
